import { Observable, forkJoin, map, of, switchMap } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';

import { Characteristic } from '../entities/characteristic.entity';
import { CharacteristicService } from './characteristic.service';
import { Consent } from '../entities/consent.entity';
import { ConsentService } from './consent.service';
import { Profile } from '../entities/profile.entity';
import { ProfileFormHandler } from '../form-handler/profile.form-handler';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  public me: Profile = null;
  private _profileFormHandler: ProfileFormHandler = new ProfileFormHandler();
  private _profileForm: FormGroup;

  constructor(
    private characteristicService: CharacteristicService,
    private consentService: ConsentService
  ) {}

  /**
   * Updates characteristics
   * @param data
   * @returns updated characteristics
   */
  updateCharacteristics(data = {}): Observable<any> {
    let observables = {};

    for (let [key, value] of Object.entries(data)) {
      if (key == 'professionalAddress') {
        const address = value;

        // Add professional address
        for (let field of ['street', 'zipCode', 'city']) {
          if (address[field] == undefined) {
            continue;
          }

          const capitalizedAddressField =
            field.charAt(0).toUpperCase() + field.slice(1);
          const addressField = 'professionalAddress' + capitalizedAddressField;

          const characteristic = this.me.characteristics.find(
            (item) =>
              item.characteristicType.code.toLowerCase() ==
              addressField.toLowerCase()
          );

          if (characteristic && characteristic.value == address[field]) {
            continue;
          }

          observables[addressField] = this.characteristicService
            .postCharacteristic({
              value: address[field].toString(),
              characteristicTypeCode: addressField,
            })
            .pipe(
              switchMap((res) =>
                this.characteristicService.getCharacteristic(addressField)
              )
            );
        }
      } else {
        const characteristic: Characteristic = this.me.characteristics.find(
          (item) =>
            item.characteristicType.code.toLowerCase() == key.toLowerCase()
        );
        observables[key] = this.characteristicService
          .postCharacteristic({
            value: value != null ? value.toString() : value,
            characteristicTypeCode: key,
          })
          .pipe(
            switchMap((res) =>
              this.characteristicService.getCharacteristic(key)
            )
          );

        if (key === 'haveRpps' && !value) {
          const i = this.me.characteristics.findIndex(
            (item) => item.characteristicType.code.toLowerCase() === 'rpps'
          );

          if (-1 < i) {
            observables['rpps'] = this.characteristicService
              .postCharacteristic({
                value: '',
                characteristicTypeCode:
                  this.me.characteristics[i].characteristicType.code,
              })
              .pipe(
                switchMap((res) => {
                  this.me.characteristics[i].value = '';
                  this.me.rpps = '';

                  return of(this.me.characteristics[i]);
                })
              );
          }
        }
      }
    }

    return forkJoin(observables);
  }

  /**
   * Updates consents
   * @param data
   * @returns updated consents
   */
  public updateConsents(data = {}): Observable<any> {
    let observables = {};

    for (let [key, value] of Object.entries(data)) {
      const consent: Consent = this.me.consents.find(
        (item) => item.consentType.toLowerCase() == key.toLowerCase()
      );
      observables[key] = this.consentService
        .postConsent({
          approved: value,
          version: '1',
          approvalDate: new Date().toISOString(),
          consentType: key,
        })
        .pipe(switchMap((res) => this.consentService.getConsent(res)));
    }

    return forkJoin(observables);
  }

  /**
   * Checks profile is completed to access to dashboard
   * @returns true if profile is completed, otherwise false
   */
  public isCompleted(): boolean {
    this._profileForm = this._profileFormHandler.init();
    this._profileFormHandler.update(this.me);

    return this._profileForm.valid;
  }
}
